import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Chip,
	Divider,
	IconButton,
	Modal,
	Typography,
} from '@mui/material';

import { InfoCard } from 'src/components/common/infocard/InfoCard';
import { IInfoColumn } from 'src/components/common/types/infocardTypes';
import PageWithLayout from 'src/components/layout/PageWithLayout';
import {
	getPatientDetails,
	PATIENT_LOADING_KEY,
} from 'src/features/patient/store/patientThunks';
import { IInferenceResult } from 'src/features/study/domain/types';
import {
	getStudyDetails,
	runInference,
} from 'src/features/study/store/studyThunks';
import { generateAIReportPDF } from 'src/features/study/utils/generateAIReportPDF';
import { RootState, AppDispatch } from 'src/store';

interface ILightboxState {
	src: string;
	label: string;
	modelName: string;
}

const AIReportPage: React.FC = () => {
	const dispatch = useDispatch<AppDispatch>();
	const { studyId, siteSlug, patientId } = useParams<{
		studyId: string;
		siteSlug: string;
		patientId: string;
	}>();

	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);
	const studyDetails = useSelector(
		(state: RootState) => state.study.studyDetails,
	);
	const patientDetails = useSelector(
		(state: RootState) => state.patient.patientDetails,
	);

	const [inferenceResults, setInferenceResults] = useState<
		IInferenceResult[]
	>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<IErrorResponse | null>(null);
	const [lightbox, setLightbox] = useState<ILightboxState | null>(null);
	const [isPdfGenerating, setIsPdfGenerating] = useState(false);

	const breadcrumbs = [
		{
			label: siteDetails?.name || 'Site Details',
			href: `/site/${siteSlug}`,
		},
		{
			label: studyDetails?.patient?.site_patient_id || 'Patient ID',
			href: `/site/${siteSlug}/patient/${studyDetails?.patient?.id || patientId}`,
		},
		{
			label: studyDetails?.patient_study_id || 'Study ID',
			href: `/site/${siteSlug}/patient/${studyDetails?.patient?.id || patientId}/study/${studyId}`,
		},
		{ label: 'AI Report' },
	];

	const infoColumns: IInfoColumn[] = [
		{
			label: 'Gender',
			value: patientDetails?.gender || '-',
		},
		{
			label: 'Age',
			value: patientDetails?.age ? patientDetails.age.toString() : '-',
		},
		{
			label: 'Address',
			value: patientDetails?.address || '-',
		},
		{
			label: 'Contact Number',
			value: patientDetails?.contact_number || '-',
		},
		{
			label: 'Occupation',
			value: patientDetails?.occupation || '-',
		},
	];

	useEffect(() => {
		if (!studyId || !siteSlug || !patientId) return;

		dispatch(getStudyDetails({ studyId: Number(studyId), siteSlug }));
		dispatch(getPatientDetails({ patientId: Number(patientId), siteSlug }));

		dispatch(
			runInference({
				studyId: Number(studyId),
				siteSlug,
				onSuccess: (data) => {
					setInferenceResults(data);
					setIsLoading(false);
				},
				onError: (err) => {
					setError(err);
					setIsLoading(false);
				},
			}),
		);
	}, [studyId, siteSlug, patientId, dispatch]);

	const renderContent = () => {
		if (isLoading) {
			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: 2,
						py: 10,
					}}
				>
					<CircularProgress sx={{ color: '#BE3327' }} />
					<Typography variant='body2' color='text.secondary'>
						Running AI inference, this may take a moment…
					</Typography>
				</Box>
			);
		}

		if (error) {
			return (
				<Alert severity='error' sx={{ borderRadius: '4px' }}>
					{error.description ||
						'An unexpected error occurred while running AI inference.'}
				</Alert>
			);
		}

		if (inferenceResults.length === 0) {
			return (
				<Typography variant='body2' color='text.secondary'>
					No inference results available for this study.
				</Typography>
			);
		}

		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
				{inferenceResults.map((result, resultIndex) => (
					<Box key={resultIndex}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: { xs: 'column', md: 'row' },
								gap: 3,
								alignItems: 'flex-start',
								py: 3,
							}}
						>
							{/* Left: image label panel */}
							<Box
								sx={{
									minWidth: { md: '180px' },
									width: { xs: '100%', md: '180px' },
									flexShrink: 0,
									display: 'flex',
									flexDirection: 'row',
									alignItems: {
										xs: 'center',
										md: 'flex-start',
									},
									gap: 1.5,
									pt: { md: 0.5 },
								}}
							>
								<Box
									sx={{
										width: 28,
										height: 28,
										borderRadius: '50%',
										backgroundColor: '#BE332715',
										border: '1.5px solid #BE332740',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										flexShrink: 0,
									}}
								>
									<Typography
										sx={{
											fontSize: '12px',
											fontWeight: 600,
											color: '#BE3327',
											lineHeight: 1,
										}}
									>
										{resultIndex + 1}
									</Typography>
								</Box>
								<Typography
									variant='subtitle1'
									sx={{
										fontWeight: 500,
										fontSize: '14px',
										color: 'rgba(0, 0, 0, 0.87)',
										lineHeight: 1.4,
										wordBreak: 'break-word',
									}}
								>
									{result.label}
								</Typography>
							</Box>

							{/* Right: responsive grid of model prediction cards */}
							<Box
								sx={{
									flex: 1,
									display: 'grid',
									gridTemplateColumns: {
										xs: '1fr',
										sm: 'repeat(2, 1fr)',
										lg:
											Object.keys(
												result.inference_results,
											).length >= 3
												? 'repeat(3, 1fr)'
												: `repeat(${Object.keys(result.inference_results).length}, 1fr)`,
									},
									gap: 2,
									width: '100%',
								}}
							>
								{Object.entries(result.inference_results).map(
									([modelName, modelResult], modelIndex) => (
										<Box
											key={modelIndex}
											sx={{
												display: 'flex',
												flexDirection: 'column',
												borderRadius: '8px',
												border: '1px solid rgba(0, 0, 0, 0.10)',
												overflow: 'hidden',
												backgroundColor: '#FAFAFA',
											}}
										>
											{/* Card header */}
											<Box
												sx={{
													display: 'flex',
													alignItems: 'center',
													justifyContent:
														'space-between',
													px: 1.5,
													py: 1,
													borderBottom:
														'1px solid rgba(0, 0, 0, 0.08)',
													backgroundColor: 'white',
												}}
											>
												<Chip
													label={modelName}
													size='small'
													sx={{
														backgroundColor:
															'#BE332715',
														color: '#BE3327',
														fontWeight: 600,
														fontSize: '11px',
														height: '22px',
													}}
												/>
												<Typography
													variant='caption'
													sx={{
														color: 'text.secondary',
														fontSize: '11px',
													}}
												>
													{modelResult.inference_time_ms.toFixed(
														0,
													)}{' '}
													ms
												</Typography>
											</Box>

											{/* Annotated image — clickable */}
											<Box
												sx={{
													position: 'relative',
													cursor: 'pointer',
													'&:hover .zoom-icon': {
														opacity: 1,
													},
													'&:hover img': {
														filter: 'brightness(0.92)',
													},
												}}
												onClick={() =>
													setLightbox({
														src: `data:image/png;base64,${modelResult.annotated_image}`,
														label: result.label,
														modelName,
													})
												}
											>
												<Box
													component='img'
													src={`data:image/png;base64,${modelResult.annotated_image}`}
													alt={`${result.label} — ${modelName}`}
													sx={{
														width: '100%',
														height: '220px',
														objectFit: 'contain',
														display: 'block',
														backgroundColor:
															'#F0F0F0',
														transition:
															'filter 0.15s ease',
													}}
												/>
												<Box
													className='zoom-icon'
													sx={{
														position: 'absolute',
														inset: 0,
														display: 'flex',
														alignItems: 'center',
														justifyContent:
															'center',
														opacity: 0,
														transition:
															'opacity 0.15s ease',
														pointerEvents: 'none',
													}}
												>
													<Box
														sx={{
															backgroundColor:
																'rgba(0,0,0,0.45)',
															borderRadius: '50%',
															width: 40,
															height: 40,
															display: 'flex',
															alignItems:
																'center',
															justifyContent:
																'center',
														}}
													>
														<ZoomInIcon
															sx={{
																color: 'white',
																fontSize: 22,
															}}
														/>
													</Box>
												</Box>
											</Box>

											{/* Card footer: detections */}
											<Box
												sx={{
													px: 1.5,
													py: 1,
													borderTop:
														'1px solid rgba(0, 0, 0, 0.08)',
													backgroundColor: 'white',
													display: 'flex',
													flexWrap: 'wrap',
													gap: 0.75,
													minHeight: '38px',
													alignItems: 'center',
												}}
											>
												{modelResult.detections
													.length === 0 ? (
													<Typography
														variant='caption'
														sx={{
															color: 'text.secondary',
															fontSize: '11px',
															fontStyle: 'italic',
														}}
													>
														No detections
													</Typography>
												) : (
													modelResult.detections.map(
														(
															detection,
															detectionIndex,
														) => (
															<Chip
																key={
																	detectionIndex
																}
																label={`${detection.class} ${(detection.confidence * 100).toFixed(1)}%`}
																size='small'
																sx={{
																	backgroundColor:
																		'#FFF3E0',
																	color: '#E65100',
																	fontWeight: 500,
																	fontSize:
																		'10px',
																	height: '20px',
																	'& .MuiChip-label':
																		{
																			px: '6px',
																		},
																}}
															/>
														),
													)
												)}
											</Box>
										</Box>
									),
								)}
							</Box>
						</Box>

						{resultIndex < inferenceResults.length - 1 && (
							<Divider />
						)}
					</Box>
				))}
			</Box>
		);
	};

	const handleDownloadPDF = () => {
		if (!patientDetails || inferenceResults.length === 0) return;
		setIsPdfGenerating(true);
		// Run after current render so the button state updates visually
		setTimeout(() => {
			try {
				generateAIReportPDF(
					patientDetails,
					inferenceResults,
					studyDetails?.patient_study_id ?? studyId ?? '',
				);
			} finally {
				setIsPdfGenerating(false);
			}
		}, 0);
	};

	const downloadButton = (
		<Button
			variant='outlined'
			size='small'
			startIcon={
				isPdfGenerating ? (
					<CircularProgress size={14} sx={{ color: '#BE3327' }} />
				) : (
					<DownloadIcon fontSize='small' />
				)
			}
			disabled={
				isLoading ||
				!!error ||
				inferenceResults.length === 0 ||
				isPdfGenerating
			}
			onClick={handleDownloadPDF}
			sx={{
				textTransform: 'none',
				borderColor: '#BE3327',
				color: '#BE3327',
				borderRadius: '20px',
				fontSize: '13px',
				minWidth: '148px',
				py: '2px',
				'&:hover': {
					borderColor: '#BE3327',
					backgroundColor: '#BE332710',
				},
				'&.Mui-disabled': {
					borderColor: 'rgba(0,0,0,0.2)',
					color: 'rgba(0,0,0,0.35)',
				},
			}}
		>
			Download PDF
		</Button>
	);

	return (
		<PageWithLayout
			breadcrumbs={breadcrumbs}
			headerRightContent={downloadButton}
		>
			<Box sx={{ p: 0.5 }}>
				<InfoCard
					title={
						patientDetails
							? `${patientDetails.first_name} ${patientDetails.last_name}`
							: '-'
					}
					columns={infoColumns}
					loadingKey={PATIENT_LOADING_KEY}
				/>

				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						padding: 3,
						backgroundColor: 'white',
						borderRadius: '4px',
						marginTop: '20px',
					}}
				>
					<Typography
						variant='h6'
						sx={{
							fontSize: '20px',
							fontWeight: 500,
							color: 'rgba(0, 0, 0, 0.87)',
						}}
					>
						AI Inference Report
					</Typography>

					{renderContent()}
				</Box>
			</Box>

			{/* Lightbox overlay */}
			<Modal
				open={lightbox !== null}
				onClose={() => setLightbox(null)}
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					p: 2,
				}}
			>
				<Box
					sx={{
						position: 'relative',
						maxWidth: '90vw',
						maxHeight: '90vh',
						outline: 'none',
						display: 'flex',
						flexDirection: 'column',
						borderRadius: '8px',
						overflow: 'hidden',
						backgroundColor: '#111',
					}}
				>
					{/* Lightbox header */}
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							px: 2,
							py: 1,
							backgroundColor: '#1a1a1a',
							gap: 2,
							flexShrink: 0,
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								overflow: 'hidden',
							}}
						>
							<Typography
								variant='body2'
								sx={{
									color: 'rgba(255,255,255,0.9)',
									fontWeight: 500,
									fontSize: '13px',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}
							>
								{lightbox?.label}
							</Typography>
							{lightbox?.modelName && (
								<Chip
									label={lightbox.modelName}
									size='small'
									sx={{
										backgroundColor: '#BE332730',
										color: '#f08070',
										fontWeight: 600,
										fontSize: '11px',
										height: '20px',
										flexShrink: 0,
									}}
								/>
							)}
						</Box>
						<IconButton
							size='small'
							onClick={() => setLightbox(null)}
							sx={{
								color: 'rgba(255,255,255,0.7)',
								flexShrink: 0,
								'&:hover': {
									color: 'white',
									backgroundColor: 'rgba(255,255,255,0.1)',
								},
							}}
						>
							<CloseIcon fontSize='small' />
						</IconButton>
					</Box>

					{/* Full-size image */}
					{lightbox && (
						<Box
							component='img'
							src={lightbox.src}
							alt={`${lightbox.label} — ${lightbox.modelName}`}
							sx={{
								maxWidth: '90vw',
								maxHeight: 'calc(90vh - 48px)',
								objectFit: 'contain',
								display: 'block',
							}}
						/>
					)}
				</Box>
			</Modal>
		</PageWithLayout>
	);
};

export default AIReportPage;
